import {LayerContext} from '@dropbox/dig-components/layer';
import {Menu} from '@dropbox/dig-components/menu';
import {atoms, Box, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, LibraryLine} from '@dropbox/dig-icons/assets';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {type KnowledgeMenuSelection, logKnowledgeMenuClosed} from 'analytics/knowledgeAnalytics';
import cx from 'classnames';
import {getKnowledgeCategoryPath, getKnowledgePagePath, ROUTE_PATHS} from 'constant';
import {t} from 'i18next';
import {useCallback, useEffect, useRef} from 'react';
import {useLocation, useNavigate} from 'react-router';
import {type ServiceNowNavCategory, type ServiceNowNavMenuNode} from 'views/knowledge/api';

import styles from './LeftNav.module.css';

// Keep the portaled flyout above the expanded left nav (z-index 13). DIG adds
// its elevated overlay offset on top of this value.
const KNOWLEDGE_MENU_LAYER_Z_INDEX = 20;

// Small grace period so the cursor can travel from the trigger to the portaled
// flyout without it closing out from under them.
const KNOWLEDGE_MENU_CLOSE_DELAY_MS = 720;

export const AI_APPLIED_HUB_URL = 'https://applied-ai-6558636255993058.aws.databricksapps.com/';

interface KnowledgeNavItemProps {
  categories: ServiceNowNavCategory[];
  chipLabel?: string;
  onNavigate?: () => void;
  /** Notifies the parent nav when the knowledge flyout opens/closes so it can
   * keep the left nav expanded while the menu is open. */
  onMenuToggle?: (isOpen: boolean) => void;
}

/**
 * Left-nav Knowledge entry. Renders like a normal left-nav item but opens the
 * knowledge categories as a flyout menu on click (replaces the old top-nav
 * category buttons).
 */
export const KnowledgeNavItem = ({
  categories,
  chipLabel,
  onNavigate,
  onMenuToggle,
}: KnowledgeNavItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname.startsWith(ROUTE_PATHS.KNOWLEDGE);
  // Tracks one flyout opening so its close can report how long it stayed open
  // and what, if anything, was picked from it.
  const menuOpenedAtRef = useRef<number | null>(null);
  const menuSelectionRef = useRef<KnowledgeMenuSelection>('none');

  const handleMenuToggle = useCallback(
    (isOpen: boolean) => {
      onMenuToggle?.(isOpen);
      if (isOpen) {
        menuOpenedAtRef.current = Date.now();
        menuSelectionRef.current = 'none';
        return;
      }
      if (menuOpenedAtRef.current !== null) {
        logKnowledgeMenuClosed({
          openMs: Date.now() - menuOpenedAtRef.current,
          selection: menuSelectionRef.current,
        });
        menuOpenedAtRef.current = null;
      }
    },
    [onMenuToggle]
  );

  const handleCategoryClick = useCallback(
    (category: ServiceNowNavCategory) => {
      menuSelectionRef.current = 'category';
      analyticsLogger().logEvent('KNOWLEDGE_CATEGORY_SELECTED', {
        category_slug: category.slug,
        category_name: category.name,
        source: 'left-nav',
      });
      navigate(getKnowledgeCategoryPath(category.slug), {state: {source: 'left-nav'}});
      onNavigate?.();
    },
    [navigate, onNavigate]
  );

  const handleNodeClick = useCallback(
    // depth 1 is a category's topic; depth 2 is a subtopic, which the
    // Knowledge home cards don't list.
    (categorySlug: string, node: ServiceNowNavMenuNode, depth: 1 | 2) => {
      menuSelectionRef.current = depth === 1 ? 'topic' : 'subtopic';
      analyticsLogger().logEvent('KNOWLEDGE_CATEGORY_SELECTED', {
        category_slug: categorySlug,
        node_id: node.id,
        node_title: node.title,
        menu_depth: depth,
        source: 'left-nav-knowledge',
      });
      navigate(getKnowledgePagePath(categorySlug, node.id), {
        state: {source: 'left-nav-knowledge'},
      });
      onNavigate?.();
    },
    [navigate, onNavigate]
  );

  const handleKnowledgeHomeClick = useCallback(() => {
    menuSelectionRef.current = 'knowledge-home';
    analyticsLogger().logEvent('KNOWLEDGE_CATEGORY_SELECTED', {
      category_slug: 'all',
      category_name: t('knowledge'),
      source: 'left-nav',
    });
    navigate(ROUTE_PATHS.KNOWLEDGE, {state: {source: 'left-nav'}});
    onNavigate?.();
  }, [navigate, onNavigate]);

  const handleAiAppliedHubClick = useCallback(() => {
    menuSelectionRef.current = 'ai-applied-hub';
    analyticsLogger().logEvent('KNOWLEDGE_CATEGORY_SELECTED', {
      category_slug: 'ai-applied-hub',
      category_name: t('ai_applied_hub'),
      source: 'left-nav',
    });
    onNavigate?.();
  }, [onNavigate]);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => cancelClose, [cancelClose]);

  return (
    <Menu.Wrapper isBlock onToggle={({isOpen}) => handleMenuToggle(isOpen)}>
      {({getContentProps, getTriggerProps, openMenu, closeMenu}) => {
        const scheduleClose = () => {
          cancelClose();
          closeTimerRef.current = setTimeout(() => {
            closeMenu({autoFocus: false});
          }, KNOWLEDGE_MENU_CLOSE_DELAY_MS);
        };

        const handleTriggerMouseEnter = () => {
          cancelClose();
          openMenu({autoFocus: false});
        };

        // Hover opens the flyout; a click goes to the Knowledge home instead
        // of toggling it.
        const handleTriggerClick = () => {
          cancelClose();
          handleKnowledgeHomeClick();
          closeMenu({autoFocus: false});
        };

        return (
          <>
            <button
              type="button"
              {...getTriggerProps()}
              onClick={handleTriggerClick}
              onMouseEnter={handleTriggerMouseEnter}
              onMouseLeave={scheduleClose}
              aria-label={t('knowledge')}
              {...withShade({
                duration: 'Instant',
                className: cx(
                  styles.itemLink,
                  {[styles.activeItemLink]: isActive},
                  atoms({
                    backgroundColor: isActive ? 'Opacity Surface' : 'transparent',
                    borderRadius: 'Medium',
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    fontFamily: 'Text',
                    fontSize: 'Text Medium',
                    paddingY: '4',
                    paddingRight: '12',
                    paddingLeft: '8',
                  })
                ),
                style: {
                  boxSizing: 'border-box',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                },
              })}
            >
              <Box display="flex" alignItems="center">
                <UIIcon className={atoms({flexShrink: 0})} isOpticallyAligned src={LibraryLine} />
                <Box marginLeft="10" display="flex" alignItems="center" style={{gap: 6}}>
                  <span className={atoms({fontWeight: isActive ? 'Strong' : 'Base'})}>
                    {t('knowledge')}
                  </span>
                  {chipLabel ? <span className={styles.navChip}>{chipLabel}</span> : null}
                </Box>
              </Box>
            </button>
            <LayerContext.Provider value={{zIndex: KNOWLEDGE_MENU_LAYER_Z_INDEX}}>
              <Menu.Content
                {...getContentProps()}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                aria-label={t('knowledge')}
                placement="right-start"
                style={{minWidth: 240}}
              >
                <Menu.Segment>
                  {categories.map((category) =>
                    category.pages.length === 0 ? (
                      <Menu.ActionItem
                        key={category.id}
                        onClick={() => handleCategoryClick(category)}
                      >
                        {category.name}
                      </Menu.ActionItem>
                    ) : (
                      <Menu.Submenu
                        key={category.id}
                        withTriggerContent={category.name}
                        withRightAccessory={
                          <UIIcon
                            src={ChevronRightLine}
                            className={atoms({color: 'Text Subtle'})}
                          />
                        }
                      >
                        <Menu.Segment>
                          <Menu.ActionItem onClick={() => handleCategoryClick(category)}>
                            {t('overview')}
                          </Menu.ActionItem>
                          {category.pages.map((page) =>
                            page.children.length > 0 ? (
                              <Menu.Submenu
                                key={page.id}
                                withTriggerContent={page.title}
                                withRightAccessory={
                                  <UIIcon
                                    src={ChevronRightLine}
                                    className={atoms({color: 'Text Subtle'})}
                                  />
                                }
                              >
                                <Menu.Segment>
                                  <Menu.ActionItem
                                    onClick={() => handleNodeClick(category.slug, page, 1)}
                                  >
                                    {t('overview')}
                                  </Menu.ActionItem>
                                  {page.children.map((childPage) => (
                                    <Menu.ActionItem
                                      key={childPage.id}
                                      onClick={() => handleNodeClick(category.slug, childPage, 2)}
                                    >
                                      {childPage.title}
                                    </Menu.ActionItem>
                                  ))}
                                </Menu.Segment>
                              </Menu.Submenu>
                            ) : (
                              <Menu.ActionItem
                                key={page.id}
                                onClick={() => handleNodeClick(category.slug, page, 1)}
                              >
                                {page.title}
                              </Menu.ActionItem>
                            )
                          )}
                        </Menu.Segment>
                      </Menu.Submenu>
                    )
                  )}
                  <Menu.LinkItem
                    href={AI_APPLIED_HUB_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${t('ai_applied_hub')}, ${t('knowledge_opens_in_new_window')}`}
                    onClick={handleAiAppliedHubClick}
                  >
                    {t('ai_applied_hub')}
                  </Menu.LinkItem>
                </Menu.Segment>
              </Menu.Content>
            </LayerContext.Provider>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
